/**
 * client/src/admin/Voices.tsx — the voices this product has, and the three ways one can be useless.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * A VOICE CAN BE ON THE BOX, IN THE CONFIGURATION, AND READY — AND THOSE ARE THREE DIFFERENT FACTS.
 *
 *  · **On the box** — spark-1 advertises it in `GET /voices`. This is how a clone Karti made on
 *    voice.karti.ai becomes visible without a deploy.
 *  · **In the configuration** — `AB_TTS_HOSTS` maps it to one of our voiceIds. `pickHost()` filters on
 *    exactly this, so a voice that is on the box and not in the configuration **cannot narrate a
 *    page**: the job fails `no-host`. It can still be auditioned, which is the point of auditioning.
 *  · **Ready** — a probe generated audio for it recently (the Readiness table, above, on the TTS tab).
 *
 * `/health` answers `status: ok` for all three states. P7a's finding was that a health endpoint does
 * not exercise the model; this screen is the same finding one object down — a config file's memory of
 * a box it has not looked at is not a list of voices.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **The bump button is the most expensive control in this product.** `voiceRev` is inside `clipKey`,
 * so raising it does not invalidate a cache — it renames every clip that voice has ever made. It is
 * priced before it acts and it requires typing the voice's name back, which is the `--yes` the CLI
 * spelling in §12 demands. Do not make it a one-tap toggle.
 *
 * ⚠️ **No new CSS.** Every class here already exists in `client/src/ui/app.css`.
 */

import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";

import { ApiError, api } from "../lib/api.ts";

/* ── the wire — `routes/voices.ts`, exactly ───────────────────────────────── */

export interface AdminVoice {
  id: string;
  label: string;
  remoteId: string;
  hostUrl: string;
  rev: number;
  variant: string;
  kind: string;
  enabled: boolean;
  isDefault: boolean;
  sampleSha: string | null;
  createdAt: number;
  mapped: boolean;
  configuredHost: string | null;
  /** `null` means **we have not looked** — never coerced to false. LAW 9's habit, applied to a screen. */
  advertised: boolean | null;
  assignable: boolean;
  reason: string | null;
  books: number;
  clips: number;
}

export interface HostDiscovery {
  url: string;
  ok: boolean;
  reason: string | null;
  advertised: string[];
  added: string[];
  restored: string[];
  disabled: string[];
  relabelled: string[];
}

export interface VoicesResponse {
  voices: AdminVoice[];
  discovery: { at: number; ageSec: number; hosts: HostDiscovery[] } | null;
  assignable: number;
  defaultVoiceId: string;
  hosts: { url: string; voices: Record<string, string> }[];
  auditionText: string;
}

interface BumpQuote {
  voiceId: string;
  label: string;
  rev: number;
  nextRev: number;
  clips: number;
  chars: number;
  estGpuSec: number;
  pages: number;
  books: number;
  confirmWord: string;
}

/* ── the screen ───────────────────────────────────────────────────────────── */

export function Voices({
  data,
  error,
  loading,
  onRefresh,
}: {
  data: VoicesResponse | null;
  error: string | null;
  loading: boolean;
  onRefresh: () => void;
}): ReactElement {
  const [busy, setBusy] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [bump, setBump] = useState<{ quote: BumpQuote; typed: string } | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrl = useRef<string | null>(null);

  // A sample is a few hundred KB and this tab is opened repeatedly. Revoking on unmount keeps the
  // blob out of the tab's memory for the rest of the session.
  useEffect(
    () => () => {
      if (objectUrl.current !== null) URL.revokeObjectURL(objectUrl.current);
    },
    [],
  );

  const discover = useCallback(async () => {
    setBusy("discover");
    setSaid(null);
    try {
      const r = await api.post<{ cycle: { hosts: HostDiscovery[] } }>("/admin/voices/discover", {});
      const added = r.cycle.hosts.flatMap((h) => h.added);
      const gone = r.cycle.hosts.flatMap((h) => h.disabled);
      const down = r.cycle.hosts.filter((h) => !h.ok);
      setSaid(
        [
          added.length > 0 ? `new: ${added.join(", ")}` : null,
          gone.length > 0 ? `no longer served: ${gone.join(", ")}` : null,
          // A host that did not answer is reported and changes NOTHING — absence of an answer is not
          // evidence a voice is gone. Saying so here is what keeps "nothing changed" honest.
          down.length > 0 ? `${down.map((h) => `${h.url}: ${h.reason ?? "no answer"}`).join(" · ")} (nothing changed)` : null,
          added.length + gone.length + down.length === 0 ? "the boxes serve exactly what they served" : null,
        ]
          .filter((s) => s !== null)
          .join(" · "),
      );
      onRefresh();
    } catch (err) {
      setSaid(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }, [onRefresh]);

  /**
   * Hear it. A raw `fetch` rather than `api.post`, because the answer is `audio/wav` and `api` exists
   * to turn a body into JSON or a typed refusal — the same seam `PronunciationSheet.hearIt` uses.
   *
   * `force: false`, so a voice that has been auditioned before is served off disk and spends nothing.
   * The generating path is metered by the same hourly bucket as the pronunciation preview.
   */
  const audition = useCallback(
    async (voiceId: string, force: boolean) => {
      setBusy(voiceId);
      setSaid(null);
      try {
        const res = await fetch(`/api/v1/admin/voices/${encodeURIComponent(voiceId)}/audition`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ force }),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { error?: string; detail?: string } | null;
          // The verbatim `detail` is shown beside the friendly line, never instead of it: "the voice
          // service is unavailable" has meant four different failures in this repository's history.
          setSaid([body?.error, body?.detail].filter(Boolean).join(" — ") || "The voice service didn't answer.");
          return;
        }
        const cached = res.headers.get("x-audition-cached") === "1";
        const blob = await res.blob();
        if (objectUrl.current !== null) URL.revokeObjectURL(objectUrl.current);
        objectUrl.current = URL.createObjectURL(blob);
        const el = audioRef.current;
        if (el !== null) {
          el.src = objectUrl.current;
          await el.play().catch(() => {
            /* the control below is a real <audio>; a browser that refuses can be pressed by hand. */
          });
        }
        setSaid(cached ? `${voiceId}: played the stored audition — no GPU spent.` : `${voiceId}: generated and stored.`);
        onRefresh();
      } catch {
        setSaid("Couldn't reach the server to hear that.");
      } finally {
        setBusy(null);
      }
    },
    [onRefresh],
  );

  const rename = useCallback(
    async (voiceId: string, label: string) => {
      setBusy(voiceId);
      try {
        await api.patch(`/admin/voices/${encodeURIComponent(voiceId)}`, { label });
        setSaid(`${voiceId} is called ${JSON.stringify(label)} now. Discovery will not overwrite a name a human chose.`);
        onRefresh();
      } catch (err) {
        setSaid(err instanceof ApiError ? err.message : String(err));
      } finally {
        setBusy(null);
      }
    },
    [onRefresh],
  );

  const setEnabled = useCallback(
    async (voiceId: string, enabled: boolean) => {
      setBusy(voiceId);
      try {
        await api.patch(`/admin/voices/${encodeURIComponent(voiceId)}`, { enabled });
        onRefresh();
      } catch (err) {
        setSaid(err instanceof ApiError ? err.message : String(err));
      } finally {
        setBusy(null);
      }
    },
    [onRefresh],
  );

  const quoteBump = useCallback(async (voiceId: string) => {
    setBusy(voiceId);
    setSaid(null);
    try {
      const r = await api.post<{ quote: BumpQuote }>(`/admin/voices/${encodeURIComponent(voiceId)}/bump`, {});
      setBump({ quote: r.quote, typed: "" });
    } catch (err) {
      setSaid(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }, []);

  const applyBump = useCallback(async () => {
    if (bump === null) return;
    setBusy(bump.quote.voiceId);
    try {
      const r = await api.post<{ applied: boolean; rev: { from: number; to: number } }>(
        `/admin/voices/${encodeURIComponent(bump.quote.voiceId)}/bump`,
        { apply: true, confirm: bump.typed },
      );
      setSaid(
        `${bump.quote.label} is revision ${r.rev.to}. Its ${bump.quote.clips} clips are staled — every page that used them re-plans as needing the voice. Nothing has been queued.`,
      );
      setBump(null);
      onRefresh();
    } catch (err) {
      setSaid(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }, [bump, onRefresh]);

  if (error !== null) return <section className="ab-panel ab-panel-bad">{error}</section>;
  if (data === null) return <section className="ab-panel">{loading ? "Asking the voice hosts…" : "No answer."}</section>;

  return (
    <>
      <section className="ab-panel">
        <div className="ab-shelf-controls">
          <h2 className="ab-h2">Voices</h2>
          <button type="button" className="ab-btn ab-btn-sm" disabled={busy !== null} onClick={() => void discover()}>
            {busy === "discover" ? "Asking…" : "Look again"}
          </button>
        </div>
        <p className="ab-hint">
          {data.assignable} of {data.voices.length} can be assigned to a book · default{" "}
          <code>{data.defaultVoiceId}</code>
          {data.discovery === null ? (
            <>
              {" "}
              · <strong>the boxes have not been asked yet</strong>
            </>
          ) : (
            <> · asked {data.discovery.ageSec < 90 ? `${data.discovery.ageSec}s` : `${Math.round(data.discovery.ageSec / 60)} min`} ago</>
          )}
        </p>
        {said !== null && <p className="ab-hint-quiet">{said}</p>}
        {/* One element, reused. On iOS an element created programmatically inside an async callback is
            no longer inside the gesture by the time it plays; a real one in the tree is. */}
        <audio ref={audioRef} preload="none" controls aria-label="The audition" />
        <p className="ab-hint-quiet">An audition says: “{data.auditionText}”</p>

        <table className="ab-table">
          <thead>
            <tr>
              <th>Voice</th>
              <th>Where</th>
              <th>Can a book use it</th>
              <th>Used by</th>
              <th>Do</th>
            </tr>
          </thead>
          <tbody>
            {data.voices.map((v) => (
              <tr key={v.id} className={v.assignable ? "is-positive" : "is-negative"}>
                <td data-label="Voice">
                  <strong>{v.label}</strong>
                  <div className="ab-table-detail">
                    <code>{v.id}</code> · rev {v.rev} · {v.variant} · {v.kind === "base" ? "the stock voice" : "a clone"}
                    {v.isDefault ? " · the server default" : ""}
                  </div>
                </td>
                <td data-label="Where">
                  <code>{v.hostUrl}</code>
                  <div className="ab-table-detail">
                    {/* The three truths, side by side. They fail separately and this is the only place
                        in the product that says so. */}
                    {v.advertised === null ? (
                      <span className="ab-chip ab-chip-warn">not asked yet</span>
                    ) : v.advertised ? (
                      <span className="ab-chip ab-chip-voice">on the box</span>
                    ) : (
                      <span className="ab-chip ab-chip-bad">not on the box</span>
                    )}{" "}
                    {v.mapped ? (
                      <span className="ab-chip ab-chip-quiet">mapped in AB_TTS_HOSTS</span>
                    ) : (
                      <span className="ab-chip ab-chip-bad">not in AB_TTS_HOSTS</span>
                    )}
                  </div>
                </td>
                <td data-label="Can a book use it">
                  {v.assignable ? (
                    <span className="ab-chip ab-chip-voice">yes</span>
                  ) : (
                    <>
                      <span className="ab-chip ab-chip-bad">no</span>
                      <div className="ab-table-detail">{v.reason}</div>
                    </>
                  )}
                </td>
                <td data-label="Used by">
                  {v.books} book{v.books === 1 ? "" : "s"}
                  <div className="ab-table-detail">{v.clips.toLocaleString()} clips</div>
                </td>
                <td data-label="Do">
                  <div className="ab-actions">
                    <button
                      type="button"
                      className="ab-btn ab-btn-sm"
                      disabled={busy !== null}
                      onClick={() => void audition(v.id, false)}
                    >
                      {v.sampleSha === null ? "Hear it" : "Play"}
                    </button>
                    <button
                      type="button"
                      className="ab-btn ab-btn-sm ab-btn-ghost"
                      disabled={busy !== null}
                      onClick={() => void audition(v.id, true)}
                    >
                      Say it again
                    </button>
                    <button
                      type="button"
                      className="ab-btn ab-btn-sm ab-btn-ghost"
                      disabled={busy !== null}
                      onClick={() => {
                        const next = window.prompt("What should this voice be called?", v.label);
                        if (next !== null && next.trim() !== "" && next !== v.label) void rename(v.id, next.trim());
                      }}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className="ab-btn ab-btn-sm ab-btn-ghost"
                      disabled={busy !== null}
                      onClick={() => void setEnabled(v.id, !v.enabled)}
                    >
                      {v.enabled ? "Stop offering" : "Offer again"}
                    </button>
                    <button
                      type="button"
                      className="ab-btn ab-btn-sm ab-btn-danger"
                      disabled={busy !== null}
                      onClick={() => void quoteBump(v.id)}
                    >
                      Re-trained…
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {bump !== null && (
        <section className="ab-panel ab-panel-bad">
          <h2 className="ab-h2">
            {bump.quote.label} was re-trained — raise it to revision {bump.quote.nextRev}?
          </h2>
          <p className="ab-lede">
            spark-1&rsquo;s <code>/voices</code> carries no version and no hash, so a re-trained clone is invisible to
            everything except this number. Raising it is the only way to tell the cache that the voice changed.
          </p>
          <dl className="ab-facts">
            <div>
              <dt>Clips staled</dt>
              <dd>{bump.quote.clips.toLocaleString()}</dd>
            </div>
            <div>
              <dt>To make again</dt>
              <dd>
                {bump.quote.estGpuSec < 60
                  ? `${bump.quote.estGpuSec} s`
                  : bump.quote.estGpuSec < 3600
                    ? `about ${Math.round(bump.quote.estGpuSec / 60)} minutes`
                    : `about ${(bump.quote.estGpuSec / 3600).toFixed(1)} hours`}{" "}
                of the voice machine
              </dd>
            </div>
            <div>
              <dt>Pages to re-join</dt>
              <dd>
                {bump.quote.pages} across {bump.quote.books} book{bump.quote.books === 1 ? "" : "s"}
              </dd>
            </div>
          </dl>
          <p className="ab-hint">
            Nothing is queued by this. The pages re-plan as needing the voice and are made when somebody presses play,
            narrates a chapter or prepares a room — at the pace the daily ceiling allows.
          </p>
          <div className="ab-field">
            <label htmlFor="ab-bump-confirm">
              Type <strong>{bump.quote.confirmWord}</strong> to confirm
            </label>
            <input
              id="ab-bump-confirm"
              value={bump.typed}
              autoComplete="off"
              onChange={(e) => setBump({ quote: bump.quote, typed: e.target.value })}
            />
          </div>
          <div className="ab-actions">
            <button
              type="button"
              className="ab-btn ab-btn-danger"
              disabled={bump.typed !== bump.quote.confirmWord || busy !== null}
              onClick={() => void applyBump()}
            >
              Raise the revision
            </button>
            <button type="button" className="ab-btn ab-btn-ghost" onClick={() => setBump(null)}>
              Leave it alone
            </button>
          </div>
        </section>
      )}

      <section className="ab-panel">
        <h2 className="ab-h2">Hosts, and what each one answered</h2>
        <p className="ab-hint">
          A voice on a box that <code>AB_TTS_HOSTS</code> does not map can be auditioned and cannot narrate — the worker
          resolves its host from that variable, and it reads its own copy on amd-server.
        </p>
        <table className="ab-table">
          <thead>
            <tr>
              <th>Host</th>
              <th>Answered</th>
              <th>Serves</th>
              <th>Mapped</th>
            </tr>
          </thead>
          <tbody>
            {data.hosts.map((h) => {
              const seen = data.discovery?.hosts.find((d) => d.url === h.url) ?? null;
              return (
                <tr key={h.url} className={seen === null ? "" : seen.ok ? "is-positive" : "is-negative"}>
                  <td data-label="Host">
                    <code>{h.url}</code>
                  </td>
                  <td data-label="Answered">
                    {seen === null ? (
                      <span className="ab-chip ab-chip-warn">not asked yet</span>
                    ) : seen.ok ? (
                      <span className="ab-chip ab-chip-voice">yes</span>
                    ) : (
                      <>
                        <span className="ab-chip ab-chip-bad">no</span>
                        <div className="ab-table-detail">{seen.reason}</div>
                      </>
                    )}
                  </td>
                  <td data-label="Serves">{seen === null ? "—" : seen.advertised.join(", ") || "nothing"}</td>
                  <td data-label="Mapped">
                    {Object.entries(h.voices).map(([remote, id]) => (
                      <div key={remote} className="ab-table-detail">
                        <code>{remote}</code> → <code>{id}</code>
                      </div>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </>
  );
}
